/**
 * Schedule item display model — mirrors core/ui/scheduleitem/ScheduleItem.kt.
 *
 * Discriminated union: Lesson | Assignment | Event. One ScheduleItem component
 * consumes the union and routes by `kind`. Adapters at the bottom convert
 * backend payloads (API `ScheduleItem`, `ClassPlannerItem`, ad-hoc
 * `ScheduleCardItem`) into the display shape.
 *
 * Optional fields (planReady, attendancePercent, marked/total, categoryName)
 * may not be present on every payload yet — the component degrades gracefully
 * when they're missing.
 */

export type ScheduleItemDisplay =
  | LessonDisplay
  | AssignmentDisplay
  | EventDisplay

export interface LessonDisplay {
  kind: 'lesson'
  id: string
  start: Date
  end: Date | null
  className: string
  classColour?: string | null
  room?: string | null
  lessonTitle?: string | null
  planReady?: boolean
  showPlanStatus?: boolean
  attendancePercent?: number | null
}

export interface AssignmentDisplay {
  kind: 'assignment'
  id: string
  due: Date
  title: string
  className?: string | null
  classColour?: string | null
  categoryName?: string | null
  categoryColor?: string | null
  markedCount?: number
  totalCount?: number
}

export interface EventDisplay {
  kind: 'event'
  id: string
  start: Date
  end: Date | null
  allDay: boolean
  title: string
  className?: string | null
  classColour?: string | null
  location?: string | null
  kindLabel?: string | null
}

// ── Adapters ─────────────────────────────────────────────────────────────────

/** Loose backend item shape — captures both `ScheduleItem` and `ScheduleCardItem`. */
export interface RawScheduleItem {
  id: string
  type: string
  title?: string
  dateTime: string
  endDateTime?: string
  classId?: string
  className?: string
  classColour?: string
  room?: string
  dateDue?: string
  location?: string
  allDay?: boolean
  /** Lesson-only. Backend populates once attendance has been recorded. */
  attendancePercent?: number | null
}

export function fromScheduleItem(item: RawScheduleItem): ScheduleItemDisplay | null {
  switch (item.type.toLowerCase()) {
    case 'lesson':
      return {
        kind: 'lesson',
        id: item.id,
        start: parseDate(item.dateTime),
        end: item.endDateTime ? parseDate(item.endDateTime) : null,
        className: item.className ?? 'Lesson',
        classColour: item.classColour,
        room: item.room,
        lessonTitle: item.title,
        attendancePercent: item.attendancePercent ?? null,
      }
    case 'assignment':
      return {
        kind: 'assignment',
        id: item.id,
        due: parseDate(item.dateDue ?? item.dateTime),
        title: item.title ?? 'Assignment',
        className: item.className,
        classColour: item.classColour,
      }
    case 'event':
      return {
        kind: 'event',
        id: item.id,
        start: parseDate(item.dateTime),
        end: item.endDateTime ? parseDate(item.endDateTime) : null,
        allDay: item.allDay === true,
        title: item.title ?? 'Event',
        className: item.className,
        classColour: item.classColour,
        location: item.location,
      }
    default:
      return null
  }
}

/** Per-class planner item from `/classes/{id}/schedule`. */
export interface ClassPlannerInput {
  type: 'Lesson' | 'Assignment' | 'Note' | string
  id: string
  dateTime: string
  endDateTime?: string
  title?: string
  room?: string
  attendancePercent?: number
  submittedCount?: number
  totalCount?: number
}

export interface ClassContext {
  className: string
  classColour?: string | null
}

export function fromClassPlannerItem(
  item: ClassPlannerInput,
  ctx: ClassContext,
): ScheduleItemDisplay | null {
  const type = item.type.toLowerCase()
  if (type === 'lesson') {
    return {
      kind: 'lesson',
      id: item.id,
      start: parseDate(item.dateTime),
      end: item.endDateTime ? parseDate(item.endDateTime) : null,
      className: ctx.className,
      classColour: ctx.classColour,
      room: item.room,
      lessonTitle: item.title,
      attendancePercent: item.attendancePercent ?? null,
    }
  }
  if (type === 'assignment') {
    return {
      kind: 'assignment',
      id: item.id,
      due: parseDate(item.dateTime),
      title: item.title ?? 'Assignment',
      className: ctx.className,
      classColour: ctx.classColour,
      markedCount: item.submittedCount ?? 0,
      totalCount: item.totalCount ?? 0,
    }
  }
  return null
}

function parseDate(value: string): Date {
  // ISO strings without timezone are interpreted as local time by Date —
  // matches how the API returns datetimes.
  return new Date(value)
}
